import React, { useRef } from 'react';

import PropTypes from 'prop-types';

import { Body, DealIndicator } from '@iris/react-components';
import clsx from 'clsx';

import { useFeatures } from '@atc/features';
import { formatNumber } from '@atc/string-fns';

import { Text } from 'reaxl';
import { InfoIcon } from 'reaxl-molecules';

/**
 * PricingIndicator displays vehicle deal indicators and pricing information with KBB Fair Purchase Price details.
 * It renders deal indicators (Great/Good/Fair) with optional market comparison and detailed explanations.
 * The component supports both inline and block layouts with feature-flag controlled KBB details display.
 *
 * @component
 * @param {Object} props - The component props
 * @param {Object} props.pricingDetail - Pricing detail object containing deal indicator and KBB information
 * @param {string} props.pricingDetail.dealIndicator - Deal indicator value ('Great', 'Good', 'Fair', etc.)
 * @param {number} [props.pricingDetail.kbbFppDelta=0] - Dollar amount below KBB Fair Purchase Price
 * @param {boolean} [props.isInline=false] - Whether to display in inline layout (horizontal) or block layout (vertical)
 * @returns {JSX.Element|null} The pricing indicator component or null if no deal indicator provided
 */
export default function PricingIndicator({ pricingDetail, isInline }) {
    const {
        kbb_fair_price_details: [isKbbFppDetailsEnabled, {
            exclude_kbb_text: excludeKbbText,
        }],
    } = useFeatures([
        'kbb_fair_price_details',
    ]);

    const { dealIndicator, kbbFppDelta = 0 } = pricingDetail;

    const ref = useRef();
    const containerRef = useRef();

    if (!dealIndicator) return null;

    const showKbbFppDetails = isKbbFppDetailsEnabled && dealIndicator && kbbFppDelta > 0;

    const lowerCasePriceIndicator = dealIndicator.toLowerCase();
    const greatPriceIndicator = lowerCasePriceIndicator === 'great';

    const dealDetails = showKbbFppDetails && (
        <div
            className={clsx('display-flex align-items-center text-size-200', { 'margin-top-2 justify-content-end': !isInline })}
        >
            {formatNumber({
                value: kbbFppDelta,
                style: 'currency',
            })}
            {' '}
            below market&nbsp;
            <InfoIcon
                placement="top"
                textColor="text-gray-base"
                className="text-size-200"
                popoverId="fairMarketPriceInfo"
                ref={ref}
                container={containerRef.current}
                infoText={(
                    <Text>
                        This vehicle is listed
                        {' '}
                        {formatNumber({
                            value: kbbFppDelta,
                            style: 'currency',
                        })}
                        {' '}
                        {excludeKbbText ? 'below the market' : (
                            <>
                                below the Kelley Blue Book
                                <sup>&reg;</sup>
                                &nbsp;Fair Purchase Price
                            </>
                        )}
                    </Text>
                )}
            />
        </div>
    );

    const staticDealDescription = (
        <>
            The price falls in the
            {`${greatPriceIndicator ? ' lower ' : ' upper '}`}
            half of the Kelley Blue Book
            <sup>&reg;</sup>
            {' '}
            Fair Market Range for similar local cars. Dealer info may not include all fees.
        </>
    );
    const dealDescription = (
        <Body
            size="sm"
            as="p"
            className="iris:text-[color:var(--sem-color-content-neutral-secondary)] iris:py-2 iris:mb-0"
        >
            <span className="iris:font-bold">
                Why is this a
                {' '}
                {lowerCasePriceIndicator}
                {' '}
                price?
            </span>
            {' '}
            {staticDealDescription}
        </Body>
    );

    const getDealIndicatorStyles = () => {
        if (isInline) {
            return 'display-flex gap-3 margin-bottom-1';
        }
        return 'w-100';
    };

    const dealIndicatorWithDetails = (
        <div className={getDealIndicatorStyles()}>
            <div className="iris:flex iris:justify-end">
                <DealIndicator variant={lowerCasePriceIndicator} />
            </div>
            {dealDetails}
        </div>
    );

    return (
        <>
            {dealIndicatorWithDetails}
            {!isInline && dealDescription}
        </>
    );
}

PricingIndicator.propTypes = {
    /**
     * Pricing detail object containing deal indicator and KBB information
     */
    pricingDetail: PropTypes.shape({
        /**
         * Deal indicator value ('Great', 'Good', 'Fair', etc.)
         */
        dealIndicator: PropTypes.string,
        /**
         * Dollar amount below KBB Fair Purchase Price
         */
        kbbFppDelta: PropTypes.number,
    }),
    /**
     * Whether to display in inline layout (horizontal) or block layout (vertical)
     */
    isInline: PropTypes.bool,
};
